import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { lexicalToPrintHtml } from '../utils/printLexical';

/**
 * useErrnotePrintData — 오답노트(errnote) 응답을 신 인쇄(v4) 입력 계약으로 만든다.
 *
 * 반환: { problems: [{problem_id, content, answer}], meta: {tree_name, edu_ver, student_name}, loading }
 *
 * 정답(answer)은 raw 문자열 그대로 넘긴다 — v4(useAnswers)의 preset 경로가 KaTeX 조판을
 * 담당하기 때문이다. 구형 ErrorNotePrint 처럼 미리 KaTeX HTML 로 렌더해 넘기면
 * v4 가 그 HTML 을 다시 KaTeX 에 넣어 조판이 깨진다.
 */
export default function useErrnotePrintData(noteId, open) {
  const { getAccessToken } = useAuth();
  const [state, setState] = useState({ problems: [], meta: null, loading: false });

  useEffect(() => {
    if (!open || !noteId) {
      setState({ problems: [], meta: null, loading: false });
      return undefined;
    }
    let alive = true;
    setState({ problems: [], meta: null, loading: true });

    const run = async () => {
      try {
        const token = await getAccessToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/exams/reviews/errnote?note_id=${noteId}`,
          { headers: { 'Authorization': token } }
        );
        const responseData = await response.json();
        if (!alive) return;
        if (responseData.status !== 'success') {
          throw new Error(`errnote 조회 실패 (status: ${responseData.status})`);
        }

        const data = responseData.data || {};
        // 구형 ErrorNotePrint 와 동일한 매칭 규칙: problems_info 순서가 문항 번호(1..N)다
        const problems = (data.problems_info || []).map((problem) => {
          const lexicalRow = (data.lexical_data || []).find(
            (l) => l.problem_id === parseInt(problem.problem_id, 10)
          );
          const answerRow = (data.answers || []).find(
            (a) => a.problem_id === parseInt(problem.problem_id, 10)
          );
          return {
            problem_id: problem.problem_id,
            content: lexicalToPrintHtml(lexicalRow?.lexical),
            answer: answerRow?.answer ?? '',
          };
        });

        setState({
          problems,
          meta: {
            tree_name: data.general_info?.tree_name || '',
            edu_ver: data.general_info?.edu_ver || '',
            student_name: data.general_info?.student_name || '',
          },
          loading: false,
        });
      } catch (error) {
        console.error('오답노트 인쇄 데이터 조회 실패:', error);
        if (alive) setState({ problems: [], meta: null, loading: false });
      }
    };

    run();
    return () => { alive = false; };
  }, [noteId, open, getAccessToken]);

  return state;
}
